import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Eye, Upload } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { AdminLayout } from '../../components/admin/layout/AdminLayout';
import { WysiwygEditor } from '../../components/admin/WysiwygEditor';
import { Button } from '../../components/admin/ui/Button';
import { Card } from '../../components/admin/ui/Card';
import { Input } from '../../components/admin/ui/Input';

interface Category {
  id: string;
  name: string;
}

interface Tag {
  id: string;
  name: string;
}

export function BlogEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [status, setStatus] = useState<'draft' | 'scheduled' | 'published'>('draft');
  const [publishedAt, setPublishedAt] = useState('');
  const [featuredImageUrl, setFeaturedImageUrl] = useState('');
  const [featuredImageAlt, setFeaturedImageAlt] = useState('');

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);

  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [canonicalUrl, setCanonicalUrl] = useState('');
  const [ogTitle, setOgTitle] = useState('');
  const [ogDescription, setOgDescription] = useState('');
  const [isIndexable, setIsIndexable] = useState(true);

  useEffect(() => {
    fetchCategoriesAndTags();
    if (!isNew && id) {
      setLoading(true);
      fetchPost();
    }
  }, [id]);

  useEffect(() => {
    if (title && !slug) {
      setSlug(generateSlug(title));
    }
  }, [title]);

  async function fetchCategoriesAndTags() {
    const { data: categoriesData } = await supabase
      .from('blog_categories')
      .select('id, name')
      .order('name');

    const { data: tagsData } = await supabase
      .from('blog_tags')
      .select('id, name')
      .order('name');

    if (categoriesData) setCategories(categoriesData);
    if (tagsData) setTags(tagsData);
  }

  async function fetchPost() {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('id', id)
      .single();

    if (error || !data) {
      console.error('Error fetching post:', error);
      navigate('/admin/blog/posts');
      return;
    }

    setTitle(data.title);
    setSlug(data.slug);
    setContent(data.content);
    setExcerpt(data.excerpt || '');
    setStatus(data.status);
    setPublishedAt(data.published_at ? data.published_at.split('T')[0] : '');
    setMetaTitle(data.meta_title || '');
    setMetaDescription(data.meta_description || '');
    setCanonicalUrl(data.canonical_url || '');
    setOgTitle(data.og_title || '');
    setOgDescription(data.og_description || '');
    setIsIndexable(data.is_indexable);

    if (data.featured_image_id) {
      const { data: media } = await supabase
        .from('media')
        .select('url, alt')
        .eq('id', data.featured_image_id)
        .single();

      if (media) {
        setFeaturedImageUrl(media.url);
        setFeaturedImageAlt(media.alt || '');
      }
    }

    const { data: postCategories } = await supabase
      .from('blog_post_categories')
      .select('category_id')
      .eq('post_id', data.id);

    if (postCategories) {
      setSelectedCategories(postCategories.map(pc => pc.category_id));
    }

    const { data: postTags } = await supabase
      .from('blog_post_tags')
      .select('tag_id')
      .eq('post_id', data.id);

    if (postTags) {
      setSelectedTags(postTags.map(pt => pt.tag_id));
    }

    setLoading(false);
  }

  function generateSlug(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  }

  async function handleSave() {
    if (!title || !slug || !content) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSaving(true);

    let featuredImageId = null;
    if (featuredImageUrl) {
      const { data: existingMedia } = await supabase
        .from('media')
        .select('id')
        .eq('url', featuredImageUrl)
        .maybeSingle();

      if (existingMedia) {
        featuredImageId = existingMedia.id;

        await supabase
          .from('media')
          .update({ alt: featuredImageAlt })
          .eq('id', featuredImageId);
      } else {
        const { data: newMedia } = await supabase
          .from('media')
          .insert({
            url: featuredImageUrl,
            alt: featuredImageAlt,
            user_id: user?.id
          })
          .select()
          .single();

        if (newMedia) {
          featuredImageId = newMedia.id;
        }
      }
    }

    const postData = {
      title,
      slug,
      content,
      excerpt,
      status,
      published_at: publishedAt || null,
      featured_image_id: featuredImageId,
      author_id: user?.id,
      meta_title: metaTitle || null,
      meta_description: metaDescription || null,
      canonical_url: canonicalUrl || null,
      og_title: ogTitle || null,
      og_description: ogDescription || null,
      is_indexable: isIndexable
    };

    let postId = id;

    if (isNew) {
      const { data, error } = await supabase
        .from('blog_posts')
        .insert(postData)
        .select()
        .single();

      if (error) {
        console.error('Error creating post:', error);
        alert('Erreur lors de la création');
        setSaving(false);
        return;
      }

      postId = data.id;
    } else {
      const { error } = await supabase
        .from('blog_posts')
        .update(postData)
        .eq('id', id);

      if (error) {
        console.error('Error updating post:', error);
        alert('Erreur lors de la mise à jour');
        setSaving(false);
        return;
      }
    }

    await supabase.from('blog_post_categories').delete().eq('post_id', postId);
    if (selectedCategories.length > 0) {
      await supabase
        .from('blog_post_categories')
        .insert(selectedCategories.map(catId => ({ post_id: postId, category_id: catId })));
    }

    await supabase.from('blog_post_tags').delete().eq('post_id', postId);
    if (selectedTags.length > 0) {
      await supabase
        .from('blog_post_tags')
        .insert(selectedTags.map(tagId => ({ post_id: postId, tag_id: tagId })));
    }

    setSaving(false);
    navigate('/admin/blog/posts');
  }

  if (loading) {
    return (
      <AdminLayout title={isNew ? 'Nouvel article' : "Éditer l'article"} description="Éditeur d'articles">
        <div className="p-8 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isNew ? 'Nouvel article' : "Éditer l'article"} description="Éditeur d'articles">
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/admin/blog/posts')}>
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isNew ? 'Nouvel article' : 'Éditer l\'article'}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {!isNew && status === 'published' && (
            <Button
              variant="secondary"
              onClick={() => window.open(`/blog/${slug}`, '_blank')}
            >
              <Eye className="w-5 h-5 mr-2" />
              Voir
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-2 space-y-6">
          <Card>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Contenu</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre *
                </label>
                <Input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Titre de l'article"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slug *
                </label>
                <Input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(generateSlug(e.target.value))}
                  placeholder="slug-de-larticle"
                />
                <p className="text-sm text-gray-500 mt-1">
                  URL: /blog/{slug || 'slug-de-larticle'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Extrait
                </label>
                <textarea
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Court résumé de l'article..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contenu *
                </label>
                <WysiwygEditor
                  value={content}
                  onChange={setContent}
                  placeholder="Écrivez votre contenu ici..."
                  minHeight={480}
                />
              </div>
            </div>
          </Card>

          <Card>
            <h2 className="text-xl font-bold text-gray-900 mb-4">SEO</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Title
                </label>
                <Input
                  type="text"
                  value={metaTitle}
                  onChange={(e) => setMetaTitle(e.target.value)}
                  placeholder={title || 'Titre SEO'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Description
                </label>
                <textarea
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Description pour les moteurs de recherche..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL Canonical
                </label>
                <Input
                  type="text"
                  value={canonicalUrl}
                  onChange={(e) => setCanonicalUrl(e.target.value)}
                  placeholder="https://trezoone.fr/blog/slug"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  OpenGraph Title
                </label>
                <Input
                  type="text"
                  value={ogTitle}
                  onChange={(e) => setOgTitle(e.target.value)}
                  placeholder={metaTitle || title || 'Titre OG'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  OpenGraph Description
                </label>
                <textarea
                  value={ogDescription}
                  onChange={(e) => setOgDescription(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Description pour les réseaux sociaux..."
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is-indexable"
                  checked={isIndexable}
                  onChange={(e) => setIsIndexable(e.target.checked)}
                  className="w-4 h-4 border-gray-300 rounded"
                />
                <label htmlFor="is-indexable" className="text-sm text-gray-700">
                  Indexable par les moteurs de recherche
                </label>
              </div>
            </div>
          </Card>
        </div>

        <div className="col-span-1 space-y-6">
          <Card>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Publication</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Statut
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as any)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="draft">Brouillon</option>
                  <option value="scheduled">Programmé</option>
                  <option value="published">Publié</option>
                </select>
              </div>

              {(status === 'scheduled' || status === 'published') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de publication
                  </label>
                  <Input
                    type="date"
                    value={publishedAt}
                    onChange={(e) => setPublishedAt(e.target.value)}
                  />
                </div>
              )}
            </div>
          </Card>

          <Card>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Image mise en avant</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL de l'image
                </label>
                <Input
                  type="text"
                  value={featuredImageUrl}
                  onChange={(e) => setFeaturedImageUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>

              {featuredImageUrl && (
                <>
                  <img
                    src={featuredImageUrl}
                    alt="Aperçu"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Texte alternatif
                    </label>
                    <Input
                      type="text"
                      value={featuredImageAlt}
                      onChange={(e) => setFeaturedImageAlt(e.target.value)}
                      placeholder="Description de l'image"
                    />
                  </div>
                </>
              )}
            </div>
          </Card>

          <Card>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Catégories</h2>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {categories.map((cat) => (
                <div key={cat.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`cat-${cat.id}`}
                    checked={selectedCategories.includes(cat.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCategories([...selectedCategories, cat.id]);
                      } else {
                        setSelectedCategories(selectedCategories.filter(id => id !== cat.id));
                      }
                    }}
                    className="w-4 h-4 border-gray-300 rounded"
                  />
                  <label htmlFor={`cat-${cat.id}`} className="text-sm text-gray-700">
                    {cat.name}
                  </label>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Tags</h2>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {tags.map((tag) => (
                <div key={tag.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`tag-${tag.id}`}
                    checked={selectedTags.includes(tag.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedTags([...selectedTags, tag.id]);
                      } else {
                        setSelectedTags(selectedTags.filter(id => id !== tag.id));
                      }
                    }}
                    className="w-4 h-4 border-gray-300 rounded"
                  />
                  <label htmlFor={`tag-${tag.id}`} className="text-sm text-gray-700">
                    {tag.name}
                  </label>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
    </AdminLayout>
  );
}
